'use client';

import { useMemo } from 'react';
import { toast } from 'sonner';
import {
  fetchDaemonTriggerPromptPreview,
  type DaemonTriggerInjectedConvention,
  type DaemonTriggerLog,
  type MergedRunnerModelsByType,
  type RunnerModelsByType,
} from '../lib/api';
import type { RunnerRequestDetailAdapter } from '../lib/runnerRequest';
import { resolveApiErrorToastMessageOrFallback } from '../lib/api/localizeApiError';
import {
  useDaemonTriggerInjectedConventions,
  useDaemonTriggerLogStream,
  useToggleDaemonTriggerFavoriteMutation,
} from './queries/useDaemonTriggers';
import { useMergedRunnerModels, useRunnerModels } from './queries/useRunnerModels';
import { useUpdateWorktreeAlias } from './useWorktreeList';
import { useRelativeTime } from './useRelativeTime';
import { useWorkspaceScope } from './useWorkspaceScope';

export type WebRunnerRequestDetailAdapter = RunnerRequestDetailAdapter<
  DaemonTriggerLog,
  DaemonTriggerInjectedConvention,
  RunnerModelsByType,
  MergedRunnerModelsByType
>;

/** 요청 상세 본문이 쓰는 웹 데이터 어댑터. React Query 훅·세션·상대시간을 닫아준다. */
export const useWebRunnerRequestDetailAdapter = (t: (key: string) => string): WebRunnerRequestDetailAdapter => {
  const { session, token, teamId, projectId, contextQuery } = useWorkspaceScope();
  const formatRelativeTime = useRelativeTime();
  return useMemo(
    () => ({
      session: {
        token,
        memberId: session?.member?.id ?? null,
        isSystemAdmin: session?.member.isSystemAdmin ?? false,
        scope: { teamId, projectId, contextQuery },
      },
      feedback: {
        success: toast.success,
        error: toast.error,
        warning: toast.warning,
        resolveError: (error, fallback) => resolveApiErrorToastMessageOrFallback(t, error, fallback),
      },
      formatRelativeTime,
      useTriggerLogs: (triggerId, options) => useDaemonTriggerLogStream(token, triggerId, options),
      useInjectedConventions: (triggerId) => useDaemonTriggerInjectedConventions(token, triggerId),
      usePublicRunnerModels: () => useRunnerModels(),
      useMergedRunnerModels: (memberToken, memberId, options) => useMergedRunnerModels(memberToken, memberId, options),
      useToggleFavoriteMutation: (mutationToken) => useToggleDaemonTriggerFavoriteMutation(mutationToken),
      useWorktreeAliasMutation: (mutationToken, mutationProjectId) =>
        useUpdateWorktreeAlias(mutationToken, mutationProjectId),
      fetchPromptPreview: (previewToken, triggerId) => fetchDaemonTriggerPromptPreview(previewToken, triggerId),
    }),
    [session, token, teamId, projectId, contextQuery, t, formatRelativeTime],
  );
};
